/**
 * User settings page component.
 */

import { useEffect, useState, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import type { SettingsFlow, UiNodeInputAttributes } from '@ory/client';
import { kratos, getFlowId, handleKratosError } from '../lib/kratos';
import { useAuth } from '../context/AuthContext';
import FormField from '../components/FormField';
import FlowMessages from '../components/FlowMessages';
import LoadingSpinner from '../components/LoadingSpinner';
import TOTPSetup from '../components/TOTPSetup';
import BackupCodes from '../components/BackupCodes';
import { config } from '../config';

interface UserService {
  id: string;
  name: string;
  description: string | null;
  granted_at: string;
}

interface UserOrg {
  id: string;
  name: string;
  role: string;
  code: string | null;
}

interface ConnectedApp {
  id: string;
  name: string;
  description: string | null;
}

interface SessionInfo {
  id: string;
  active: boolean;
  authenticated_at: string | null;
  expires_at: string | null;
  devices: Array<{ ip_address?: string; user_agent?: string }>;
}

export default function Settings() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, identity, kycLevel, refresh: refreshAuth } = useAuth();
  const [flow, setFlow] = useState<SettingsFlow | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'profile' | 'password' | 'security' | 'access'>('profile');
  const [isAdmin, setIsAdmin] = useState(false);
  const [userServices, setUserServices] = useState<UserService[]>([]);
  const [userOrgs, setUserOrgs] = useState<UserOrg[]>([]);
  const [loadingAccess, setLoadingAccess] = useState(true);
  const [defaultOrgId, setDefaultOrgId] = useState<string | null>(null);
  const [settingDefault, setSettingDefault] = useState<string | null>(null);
  const [showTOTPSetup, setShowTOTPSetup] = useState(false);
  const [totpEnabled, setTotpEnabled] = useState(false);
  const [connectedApps, setConnectedApps] = useState<ConnectedApp[]>([]);
  const [sessions, setSessions] = useState<SessionInfo[]>([]);
  const [revokingSession, setRevokingSession] = useState<string | null>(null);
  const [kycStatus, setKycStatus] = useState<string>('none');
  const [kycVerifiedAt, setKycVerifiedAt] = useState<string | null>(null);
  const [setupPasskey, setSetupPasskey] = useState(false);
  const [passkeyReturnTo, setPasskeyReturnTo] = useState<string | null>(null);
  const [kycInitiating, setKycInitiating] = useState(false);

  const getFlowCsrfToken = (flowData: SettingsFlow): string | undefined => {
    const csrfNode = flowData.ui.nodes.find((node) => {
      const attrs = node.attributes as UiNodeInputAttributes;
      return attrs.name === 'csrf_token';
    });
    return csrfNode ? (csrfNode.attributes as UiNodeInputAttributes).value as string : undefined;
  };

  const checkTotpStatus = (flowData: SettingsFlow) => {
    // Check if TOTP is already enabled by looking for totp_unlink node
    const totpNodes = flowData.ui.nodes.filter((n) => n.group === 'totp');
    const hasUnlinkNode = totpNodes.some((node) => {
      const attrs = node.attributes as UiNodeInputAttributes;
      return attrs.name === 'totp_unlink';
    });
    setTotpEnabled(hasUnlinkNode);
  };

  const initializeFlow = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const flowId = getFlowId(location.search);

      if (flowId) {
        try {
          const { data } = await kratos.getSettingsFlow({ id: flowId });
          setFlow(data);
          checkTotpStatus(data);
          return;
        } catch {
          // Flow expired or invalid — create a fresh one below
        }
      }

      const { data } = await kratos.createBrowserSettingsFlow();
      navigate(`/settings?flow=${data.id}`, { replace: true });
      setFlow(data);
      checkTotpStatus(data);
    } catch (err) {
      setError(handleKratosError(err));
    } finally {
      setIsLoading(false);
    }
  }, [location.search, navigate]);

  // Handle tab query param (for return from re-auth)
  // When returning with ?tab=security, we need a fresh flow, so clear any existing flow ID
  useEffect(() => {
    // QR login redirect — recovery link landed us on /settings, redirect to original destination
    const qrReturnTo = sessionStorage.getItem('qr_login_return_to');
    if (qrReturnTo) {
      sessionStorage.removeItem('qr_login_return_to');
      window.location.href = qrReturnTo;
      return;
    }

    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    const flowId = params.get('flow');

    if (tabParam === 'security' || tabParam === 'profile' || tabParam === 'password' || tabParam === 'access') {
      setActiveTab(tabParam);

      // If we have a tab param but also a flow ID, clear the flow ID to force a fresh flow
      // This is needed after re-authentication for privileged actions
      if (flowId && tabParam) {
        navigate(`/settings?tab=${tabParam}`, { replace: true });
      }
    }

    // Post-registration passkey setup prompt
    if (params.get('setup_passkey') === '1') {
      setActiveTab('security');
      setSetupPasskey(true);
      setPasskeyReturnTo(params.get('return_to') || '/');
    }
  }, [location.search, navigate]);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login?return_to=/settings', { replace: true });
      return;
    }

    if (!authLoading && isAuthenticated) {
      initializeFlow();
      // Check admin status
      fetch(`${config.gatewayUrl}/v1/admin/check`, { credentials: 'include' })
        .then(res => res.json())
        .then(data => setIsAdmin(data.is_admin || false))
        .catch(() => setIsAdmin(false));

      // Load user services and organizations
      setLoadingAccess(true);
      Promise.all([
        fetch(`${config.gatewayUrl}/v1/access/me/services`, { credentials: 'include' })
          .then(res => res.ok ? res.json() : { services: [] })
          .then(data => setUserServices(data.services || []))
          .catch(() => setUserServices([])),
        fetch(`${config.gatewayUrl}/v1/access/me/organizations`, { credentials: 'include' })
          .then(res => res.ok ? res.json() : { organizations: [], default_organization: null })
          .then(data => {
            setUserOrgs(data.organizations || []);
            setDefaultOrgId(data.default_organization || null);
          })
          .catch(() => setUserOrgs([])),
        fetch(`${config.gatewayUrl}/v1/access/me/apps`, { credentials: 'include' })
          .then(res => res.ok ? res.json() : { apps: [] })
          .then(data => setConnectedApps(data.apps || []))
          .catch(() => setConnectedApps([])),
        fetch(`${config.gatewayUrl}/v1/access/me/sessions`, { credentials: 'include' })
          .then(res => res.ok ? res.json() : { sessions: [] })
          .then(data => setSessions(data.sessions || []))
          .catch(() => setSessions([])),
        fetch(`${config.gatewayUrl}/v1/kyc/status`, { credentials: 'include' })
          .then(res => res.ok ? res.json() : { kyc_level: 0, status: 'none' })
          .then(data => {
            setKycStatus(data.status || 'none');
            const verified = (data.verifications || []).find((v: { status: string }) => v.status === 'verified');
            setKycVerifiedAt(verified?.verified_at || null);
          })
          .catch(() => {}),
      ]).finally(() => setLoadingAccess(false));
    }
  }, [initializeFlow, isAuthenticated, authLoading, navigate]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>, group: string) => {
    event.preventDefault();
    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    const formData = new FormData(event.currentTarget);
    const values: Record<string, string> = {};
    formData.forEach((value, key) => {
      values[key] = value.toString();
    });

    // Ensure csrf_token is always present — extract from flow if missing
    if (!values.csrf_token && flow) {
      const token = getFlowCsrfToken(flow);
      if (token) values.csrf_token = token;
    }

    try {
      const { data } = await kratos.updateSettingsFlow({
        flow: flow!.id,
        updateSettingsFlowBody: values as unknown as Parameters<typeof kratos.updateSettingsFlow>[0]['updateSettingsFlowBody'],
      });

      setFlow(data);

      // Check for success state
      if (data.state === 'success') {
        setSuccess(group === 'password' ? 'Password updated successfully!' : 'Profile updated successfully!');
      }
    } catch (err) {
      const errorMessage = handleKratosError(err);
      if (errorMessage.includes('expired') || errorMessage.includes('invalid')) {
        initializeFlow();
      }
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const setDefaultOrg = async (orgId: string) => {
    setSettingDefault(orgId);
    setError(null);
    try {
      const response = await fetch(`${config.gatewayUrl}/v1/admin/users/me/default-organization`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ organization_id: orgId }),
      });

      if (response.ok) {
        setDefaultOrgId(orgId);
        setSuccess('Default organization updated!');
        refreshAuth(); // Refresh auth context to update org state
      } else {
        setError('Failed to set default organization');
      }
    } catch (err) {
      setError('Failed to set default organization');
    } finally {
      setSettingDefault(null);
    }
  };

  if (isLoading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner text="Loading..." />
      </div>
    );
  }

  if (error && !flow) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="card max-w-md w-full text-center">
          <h1 className="text-xl font-bold text-red-600 dark:text-red-400 mb-2">Error</h1>
          <p className="text-content-secondary mb-4">{error}</p>
          <button onClick={initializeFlow} className="btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Group nodes
  const profileNodes = flow?.ui.nodes.filter((n) => n.group === 'profile') || [];
  const passwordNodes = flow?.ui.nodes.filter((n) => n.group === 'password') || [];
  const defaultNodes = flow?.ui.nodes.filter((n) => n.group === 'default') || [];

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-content-primary">Account Settings</h1>
          <p className="text-content-secondary mt-1">
            Manage your profile and security settings
          </p>
        </div>

        {/* User info card */}
        <div className="card mb-6">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 rounded-full bg-orbital-600 flex items-center justify-center">
              <span className="text-2xl font-bold text-white">
                {(() => {
                  const name = identity?.traits?.name as { first?: string; last?: string } | undefined;
                  const email = identity?.traits?.email as string | undefined;
                  return name?.first?.charAt(0)?.toUpperCase() ||
                         email?.charAt(0)?.toUpperCase() ||
                         'U';
                })()}
              </span>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-content-primary">
                {(() => {
                  const name = identity?.traits?.name as { first?: string; last?: string } | undefined;
                  return name ? `${name.first || ''} ${name.last || ''}`.trim() : 'User';
                })()}
              </h2>
              <p className="text-content-secondary">{identity?.traits?.email as string}</p>
              {isAdmin && (
                <Link to="/admin" className="text-sm text-orbital-600 hover:text-orbital-800 dark:text-orbital-400 dark:hover:text-orbital-300 font-medium">
                  Admin Dashboard →
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-border mb-6">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('profile')}
              className={`pb-4 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'profile'
                  ? 'border-orbital-600 text-orbital-600'
                  : 'border-transparent text-content-tertiary hover:text-content-primary'
              }`}
            >
              Profile
            </button>
            <button
              onClick={() => setActiveTab('password')}
              className={`pb-4 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'password'
                  ? 'border-orbital-600 text-orbital-600'
                  : 'border-transparent text-content-tertiary hover:text-content-primary'
              }`}
            >
              Password
            </button>
            <button
              onClick={() => setActiveTab('security')}
              className={`pb-4 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'security'
                  ? 'border-orbital-600 text-orbital-600'
                  : 'border-transparent text-content-tertiary hover:text-content-primary'
              }`}
            >
              Security
            </button>
            <button
              onClick={() => setActiveTab('access')}
              className={`pb-4 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'access'
                  ? 'border-orbital-600 text-orbital-600'
                  : 'border-transparent text-content-tertiary hover:text-content-primary'
              }`}
            >
              Access
            </button>
          </div>
        </div>

        <FlowMessages messages={flow?.ui.messages} />

        {error && (
          <div className="bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-4 py-3 rounded-lg mb-4 text-sm">
            {success}
          </div>
        )}

        {/* Profile form */}
        {activeTab === 'profile' && (
          <div className="card">
            <h3 className="text-lg font-medium text-content-primary mb-4">Profile Information</h3>
            <form onSubmit={(e) => handleSubmit(e, 'profile')} className="space-y-4">
              {/* Hidden fields */}
              {defaultNodes
                .filter((n) => (n.attributes as { type?: string }).type === 'hidden')
                .map((node, i) => (
                  <FormField key={i} node={node} disabled={isSubmitting} />
                ))}

              {/* Profile fields — filter out system-managed traits, custom render account_type */}
              {profileNodes
                .filter((n) => {
                  const attrs = n.attributes as { type?: string; name?: string };
                  if (attrs.type === 'submit') return false;
                  // System-managed fields — not user-editable
                  const hiddenFields = ['traits.kyc_level', 'traits.is_admin', 'traits.default_organization'];
                  if (hiddenFields.includes(attrs.name || '')) return false;
                  // account_type rendered separately as dropdown
                  if (attrs.name === 'traits.account_type') return false;
                  return true;
                })
                .map((node, i) => (
                  <FormField key={i} node={node} disabled={isSubmitting} />
                ))}

              {/* Account Type — dropdown instead of free text */}
              {profileNodes.some((n) => (n.attributes as { name?: string }).name === 'traits.account_type') && (
                <div className="space-y-1">
                  <label htmlFor="traits.account_type" className="block text-sm font-medium text-content-primary">
                    Account Type
                  </label>
                  <select
                    id="traits.account_type"
                    name="traits.account_type"
                    defaultValue={
                      (profileNodes.find((n) => (n.attributes as { name?: string }).name === 'traits.account_type')
                        ?.attributes as { value?: string })?.value || 'individual'
                    }
                    disabled={isSubmitting}
                    className="input-field"
                  >
                    <option value="individual">Individual</option>
                    <option value="business">Business</option>
                  </select>
                </div>
              )}

              {/* KYC Level + Is Admin — read-only display, hidden inputs to preserve values */}
              {profileNodes
                .filter((n) => {
                  const name = (n.attributes as { name?: string }).name || '';
                  return name === 'traits.kyc_level' || name === 'traits.is_admin' || name === 'traits.default_organization';
                })
                .map((node, i) => (
                  <input
                    key={`hidden-${i}`}
                    type="hidden"
                    name={(node.attributes as { name?: string }).name}
                    value={(node.attributes as { value?: string | number | boolean }).value as string ?? ''}
                  />
                ))}

              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary flex items-center justify-center"
              >
                {isSubmitting ? <LoadingSpinner size="sm" /> : 'Update Profile'}
              </button>
            </form>
          </div>
        )}

        {/* Password form */}
        {activeTab === 'password' && (
          <div className="card">
            <h3 className="text-lg font-medium text-content-primary mb-4">Change Password</h3>
            <form onSubmit={(e) => handleSubmit(e, 'password')} className="space-y-4">
              {/* Hidden fields */}
              {defaultNodes
                .filter((n) => (n.attributes as { type?: string }).type === 'hidden')
                .map((node, i) => (
                  <FormField key={i} node={node} disabled={isSubmitting} />
                ))}

              {/* Password fields */}
              {passwordNodes
                .filter((n) => (n.attributes as { type?: string }).type !== 'submit')
                .map((node, i) => (
                  <FormField key={i} node={node} disabled={isSubmitting} />
                ))}

              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary flex items-center justify-center"
              >
                {isSubmitting ? <LoadingSpinner size="sm" /> : 'Update Password'}
              </button>
            </form>
          </div>
        )}

        {/* Security tab */}
        {activeTab === 'security' && (
          <div className="space-y-6">
            {/* Post-registration passkey setup banner */}
            {setupPasskey && (
              <div className="card border-2 border-orbital-400 dark:border-orbital-500">
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-12 h-12 rounded-full bg-orbital-100 dark:bg-orbital-900 flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-orbital-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-content-primary">Secure your account with a passkey</h3>
                    <p className="text-sm text-content-secondary">
                      Sign in faster with your fingerprint, face, or security key — no password needed.
                    </p>
                  </div>
                </div>
                <p className="text-xs text-content-tertiary mb-4">
                  You can register a passkey below, or skip this step and add one later from Settings.
                </p>
                <button
                  onClick={() => {
                    setSetupPasskey(false);
                    if (passkeyReturnTo) {
                      window.location.href = passkeyReturnTo;
                    }
                  }}
                  className="text-sm text-content-tertiary hover:text-content-secondary underline"
                >
                  Skip for now
                </button>
              </div>
            )}

            {/* Two-Factor Authentication */}
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-medium text-content-primary">Two-Factor Authentication</h3>
                  <p className="text-sm text-content-tertiary">
                    Add an extra layer of security to your account using an authenticator app.
                  </p>
                </div>
                {totpEnabled && (
                  <span className="px-3 py-1 text-sm font-medium rounded-full bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300">
                    Enabled
                  </span>
                )}
              </div>

              {showTOTPSetup && flow ? (
                <TOTPSetup
                  flowId={flow.id}
                  onSuccess={() => {
                    setShowTOTPSetup(false);
                    setTotpEnabled(true);
                    initializeFlow(); // Refresh flow to get updated state
                  }}
                  onCancel={() => setShowTOTPSetup(false)}
                />
              ) : totpEnabled ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/30 px-4 py-3 rounded-lg">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    <span>Your account is protected with two-factor authentication.</span>
                  </div>
                  <button
                    onClick={async () => {
                      if (!flow) return;
                      if (!confirm('Disable two-factor authentication? Your account will be less secure.')) return;
                      setError(null);
                      try {
                        const { data } = await kratos.updateSettingsFlow({
                          flow: flow.id,
                          updateSettingsFlowBody: {
                            method: 'totp',
                            csrf_token: getFlowCsrfToken(flow),
                            totp_unlink: true,
                          } as unknown as Parameters<typeof kratos.updateSettingsFlow>[0]['updateSettingsFlowBody'],
                        });
                        setFlow(data);
                        setTotpEnabled(false);
                        setSuccess('Two-factor authentication has been disabled.');
                      } catch (err) {
                        setError(handleKratosError(err));
                      }
                    }}
                    className="text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                  >
                    Disable 2FA
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowTOTPSetup(true)}
                  className="btn-primary"
                >
                  Enable Two-Factor Authentication
                </button>
              )}
            </div>

            {/* Backup Codes */}
            {flow && (
              <div className="card">
                <BackupCodes
                  flowId={flow.id}
                  onRegenerate={() => {
                    setSuccess('Backup codes regenerated successfully!');
                  }}
                />
              </div>
            )}

            {/* Passkeys (WebAuthn) */}
            {flow && (() => {
              const webauthnNodes = flow.ui.nodes.filter((n) => n.group === 'webauthn');
              const hasWebauthn = webauthnNodes.length > 0;
              // Registered passkeys have webauthn_remove buttons
              const registeredKeys = webauthnNodes.filter((n) => {
                const attrs = n.attributes as UiNodeInputAttributes;
                return attrs.name === 'webauthn_remove';
              });
              // Registration trigger
              const registerTrigger = webauthnNodes.find((n) => {
                const attrs = n.attributes as UiNodeInputAttributes;
                return attrs.name === 'webauthn_register_trigger';
              });
              if (!hasWebauthn) return null;

              return (
                <div className="card">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-medium text-content-primary">Passkeys</h3>
                      <p className="text-sm text-content-tertiary">
                        Sign in with fingerprint, face, or security key — no password needed.
                      </p>
                    </div>
                    {registeredKeys.length > 0 && (
                      <span className="px-3 py-1 text-sm font-medium rounded-full bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300">
                        {registeredKeys.length} registered
                      </span>
                    )}
                  </div>

                  {/* List registered passkeys */}
                  {registeredKeys.length > 0 && (
                    <div className="space-y-2 mb-4">
                      {registeredKeys.map((node, i) => {
                        const attrs = node.attributes as UiNodeInputAttributes;
                        const label = node.meta?.label?.text || `Passkey ${i + 1}`;
                        return (
                          <div key={i} className="flex items-center justify-between border rounded-lg px-4 py-3">
                            <div className="flex items-center gap-3">
                              <svg className="w-5 h-5 text-orbital-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
                              </svg>
                              <span className="text-sm text-content-primary">{label}</span>
                            </div>
                            <form
                              action={flow.ui.action}
                              method={flow.ui.method}
                              onSubmit={async (e) => {
                                e.preventDefault();
                                try {
                                  await kratos.updateSettingsFlow({
                                    flow: flow.id,
                                    updateSettingsFlowBody: {
                                      method: 'webauthn',
                                      csrf_token: getFlowCsrfToken(flow),
                                      webauthn_remove: attrs.value as string,
                                    } as Parameters<typeof kratos.updateSettingsFlow>[0]['updateSettingsFlowBody'],
                                  });
                                  initializeFlow();
                                  setSuccess('Passkey removed');
                                } catch (err) {
                                  setError(handleKratosError(err));
                                }
                              }}
                            >
                              <button
                                type="submit"
                                className="text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                              >
                                Remove
                              </button>
                            </form>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Register new passkey — browser-flow-only (WebAuthn excluded from API/AJAX in Kratos) */}
                  {registerTrigger && (() => {
                    const triggerAttrs = registerTrigger.attributes as unknown as { onclick?: string };
                    const onclickCode = triggerAttrs.onclick || '';
                    const optMatch = onclickCode.match(/window\.__oryWebAuthnRegistration\((.+)\)/s);
                    const webauthnRegisterNode = webauthnNodes.find((n) => {
                      const a = n.attributes as UiNodeInputAttributes;
                      return a.name === 'webauthn_register' && a.type === 'hidden';
                    });

                    return (
                      <form
                        id="webauthn-register-form"
                        action={flow.ui.action}
                        method="POST"
                      >
                        {/* Default hidden fields (csrf_token etc.) */}
                        {flow.ui.nodes
                          .filter((n) => n.group === 'default' && (n.attributes as { type?: string }).type === 'hidden')
                          .map((node, i) => {
                            const attrs = node.attributes as UiNodeInputAttributes;
                            return <input key={i} type="hidden" name={attrs.name} value={attrs.value as string} />;
                          })}
                        {/* WebAuthn register — filled by JS after ceremony */}
                        {webauthnRegisterNode && (
                          <input type="hidden" name="webauthn_register" id="webauthn_register" defaultValue="" />
                        )}
                        <div className="mb-3">
                          <label htmlFor="webauthn_register_displayname" className="block text-sm font-medium text-content-primary mb-1">
                            Passkey name
                          </label>
                          <input
                            id="webauthn_register_displayname"
                            type="text"
                            name="webauthn_register_displayname"
                            defaultValue="My Passkey"
                            className="input-field"
                          />
                        </div>
                        <button
                          type="button"
                          name="webauthn_register_trigger"
                          className="btn-primary"
                          onClick={async () => {
                            if (!optMatch) { setError('WebAuthn not available'); return; }
                            try {
                              // eslint-disable-next-line no-eval
                              const opt = eval(`(${optMatch[1]})`);
                              const b64Decode = (v: string) =>
                                Uint8Array.from(atob(v.replace(/-/g, '+').replace(/_/g, '/')), c => c.charCodeAt(0));
                              const b64Encode = (v: ArrayBuffer) =>
                                btoa(String.fromCharCode(...new Uint8Array(v)))
                                  .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');

                              opt.publicKey.challenge = b64Decode(opt.publicKey.challenge);
                              opt.publicKey.user.id = b64Decode(opt.publicKey.user.id);
                              if (opt.publicKey.excludeCredentials) {
                                opt.publicKey.excludeCredentials = opt.publicKey.excludeCredentials.map(
                                  (c: { id: string }) => ({ ...c, id: b64Decode(c.id) })
                                );
                              }

                              // Run the browser WebAuthn ceremony
                              const credential = await navigator.credentials.create(opt) as PublicKeyCredential;
                              const response = credential.response as AuthenticatorAttestationResponse;

                              // Fill the hidden field with the attestation
                              const registerField = document.getElementById('webauthn_register') as HTMLInputElement;
                              registerField.value = JSON.stringify({
                                id: credential.id,
                                rawId: b64Encode(credential.rawId),
                                type: credential.type,
                                response: {
                                  attestationObject: b64Encode(response.attestationObject),
                                  clientDataJSON: b64Encode(response.clientDataJSON),
                                },
                              });

                              // Submit as native form (form-urlencoded) — browser flow path gets 303
                              (document.getElementById('webauthn-register-form') as HTMLFormElement).submit();
                            } catch (err) {
                              if (err instanceof DOMException && err.name === 'NotAllowedError') {
                                setError('Passkey registration was cancelled.');
                              } else {
                                setError(handleKratosError(err));
                              }
                            }
                          }}
                        >
                          Add Passkey
                        </button>
                      </form>
                    );
                  })()}
                </div>
              );
            })()}

            {/* Identity Verification (KYC) */}
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-medium text-content-primary">Identity Verification</h3>
                  <p className="text-sm text-content-tertiary">
                    Verify your identity to access higher-trust features.
                  </p>
                </div>
                {kycLevel > 0 && (
                  <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                    kycLevel >= 3 ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300' :
                    kycLevel >= 2 ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300' :
                    'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300'
                  }`}>
                    {kycLevel === 1 ? 'Basic' : kycLevel === 2 ? 'Enhanced' : 'Full'} Verified
                  </span>
                )}
              </div>

              {kycStatus === 'pending' ? (
                <div className="flex items-center gap-3 text-yellow-700 dark:text-yellow-300 bg-yellow-50 dark:bg-yellow-900/30 px-4 py-3 rounded-lg">
                  <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  <span>Verification in progress. This may take a few minutes.</span>
                </div>
              ) : kycLevel > 0 ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/30 px-4 py-3 rounded-lg">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    <div>
                      <span>Identity verified at level {kycLevel} ({kycLevel === 1 ? 'Basic' : kycLevel === 2 ? 'Enhanced' : 'Full'}).</span>
                      {kycVerifiedAt && (
                        <p className="text-xs mt-1 opacity-80">
                          Verified on {new Date(kycVerifiedAt).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                  {kycLevel < 3 && (
                    <button
                      onClick={async () => {
                        setKycInitiating(true);
                        setError(null);
                        try {
                          const res = await fetch(`${config.gatewayUrl}/v1/kyc/verify`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            credentials: 'include',
                            body: JSON.stringify({ level: kycLevel + 1 }),
                          });
                          if (res.ok) {
                            const data = await res.json();
                            setKycStatus(data.status);
                            if (data.redirect_url) {
                              window.open(data.redirect_url, '_blank');
                            } else {
                              setSuccess('Verification request submitted. An admin will review it shortly.');
                            }
                          } else {
                            setError('Failed to initiate verification');
                          }
                        } catch {
                          setError('Failed to initiate verification');
                        } finally {
                          setKycInitiating(false);
                        }
                      }}
                      disabled={kycInitiating}
                      className="text-sm text-orbital-600 hover:text-orbital-800 dark:text-orbital-400 dark:hover:text-orbital-300 disabled:opacity-50"
                    >
                      {kycInitiating ? 'Submitting...' : `Upgrade to Level ${kycLevel + 1}`}
                    </button>
                  )}
                </div>
              ) : (
                <button
                  onClick={async () => {
                    setKycInitiating(true);
                    setError(null);
                    try {
                      const res = await fetch(`${config.gatewayUrl}/v1/kyc/verify`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({ level: 1 }),
                      });
                      if (res.ok) {
                        const data = await res.json();
                        setKycStatus(data.status);
                        if (data.redirect_url) {
                          window.open(data.redirect_url, '_blank');
                        } else {
                          setSuccess('Verification request submitted. An admin will review it shortly.');
                        }
                      } else {
                        setError('Failed to initiate verification');
                      }
                    } catch {
                      setError('Failed to initiate verification');
                    } finally {
                      setKycInitiating(false);
                    }
                  }}
                  disabled={kycInitiating}
                  className="btn-primary"
                >
                  {kycInitiating ? 'Submitting...' : 'Start Verification'}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Access tab */}
        {activeTab === 'access' && (
          <div className="space-y-6">
            {loadingAccess ? (
              <div className="text-center py-8">
                <LoadingSpinner text="Loading access information..." />
              </div>
            ) : (
              <>
                {/* Organizations */}
                <div className="card">
                  <h3 className="text-lg font-medium text-content-primary mb-4">My Organizations</h3>
                  {userOrgs.length === 0 ? (
                    <p className="text-content-tertiary text-center py-4">
                      You are not a member of any organizations.
                    </p>
                  ) : (
                    <div className="grid gap-4 sm:grid-cols-2">
                      {userOrgs.map((org) => (
                        <div key={org.id} className={`border rounded-lg p-4 ${defaultOrgId === org.id ? 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/30' : ''}`}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium text-content-primary">{org.name}</h4>
                              {defaultOrgId === org.id && (
                                <span className="px-2 py-0.5 text-xs font-medium rounded bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300">
                                  Default
                                </span>
                              )}
                            </div>
                            <span className={`px-2 py-1 text-xs font-medium rounded ${
                              org.role === 'owner' ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300' :
                              org.role === 'admin' ? 'bg-orbital-100 dark:bg-orbital-900/40 text-orbital-800 dark:text-orbital-300' :
                              'bg-surface-tertiary text-content-secondary'
                            }`}>
                              {org.role.charAt(0).toUpperCase() + org.role.slice(1)}
                            </span>
                          </div>
                          {org.code && (
                            <p className="text-sm text-content-tertiary mb-2">
                              Invite Code: <span className="font-mono font-medium">{org.code}</span>
                            </p>
                          )}
                          {defaultOrgId !== org.id && (
                            <button
                              onClick={() => setDefaultOrg(org.id)}
                              disabled={settingDefault !== null}
                              className="text-sm text-orbital-600 hover:text-orbital-800 dark:text-orbital-400 dark:hover:text-orbital-300 disabled:opacity-50"
                            >
                              {settingDefault === org.id ? 'Setting...' : 'Set as Default'}
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Services */}
                <div className="card">
                  <h3 className="text-lg font-medium text-content-primary mb-4">My Service Access</h3>
                  {userServices.length === 0 ? (
                    <p className="text-content-tertiary text-center py-4">
                      You don't have access to any services yet. Contact your administrator to request access.
                    </p>
                  ) : (
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {userServices.map((service) => (
                        <div key={service.id} className="border rounded-lg p-4 hover:border-orbital-300 transition-colors">
                          <div className="flex items-center space-x-3 mb-2">
                            <div className="w-10 h-10 rounded-lg bg-orbital-100 dark:bg-orbital-900 flex items-center justify-center">
                              <svg className="w-5 h-5 text-orbital-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                              </svg>
                            </div>
                            <div>
                              <h4 className="font-medium text-content-primary">{service.name}</h4>
                              <p className="text-xs text-content-tertiary">{service.id}</p>
                            </div>
                          </div>
                          {service.description && (
                            <p className="text-sm text-content-secondary mb-2">{service.description}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Connected Apps */}
                <div className="card">
                  <h3 className="text-lg font-medium text-content-primary mb-4">Connected Applications</h3>
                  {connectedApps.length === 0 ? (
                    <p className="text-content-tertiary text-center py-4">
                      No applications connected to your account.
                    </p>
                  ) : (
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {connectedApps.map((app) => (
                        <div key={app.id} className="border rounded-lg p-4">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/40 flex items-center justify-center">
                              <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                              </svg>
                            </div>
                            <div>
                              <h4 className="font-medium text-content-primary">{app.name}</h4>
                              {app.description && (
                                <p className="text-xs text-content-tertiary">{app.description}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Devices link */}
                <div className="card">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-medium text-content-primary">My Devices</h3>
                      <p className="text-sm text-content-tertiary mt-1">
                        Manage IoT devices, agents, and tunnel clients
                      </p>
                    </div>
                    <Link
                      to="/devices"
                      className="btn-primary text-sm"
                    >
                      Manage Devices
                    </Link>
                  </div>
                </div>

                {/* Active Sessions */}
                <div className="card">
                  <h3 className="text-lg font-medium text-content-primary mb-4">Active Sessions</h3>
                  {sessions.length === 0 ? (
                    <p className="text-content-tertiary text-center py-4">
                      No active sessions found.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {sessions.map((session) => (
                        <div key={session.id} className="border rounded-lg p-4 flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <div className={`w-2 h-2 rounded-full ${session.active ? 'bg-green-500' : 'bg-content-tertiary'}`} />
                              <span className="text-sm font-medium text-content-primary">
                                {session.devices?.[0]?.user_agent
                                  ? (session.devices[0].user_agent.includes('Firefox') ? 'Firefox'
                                    : session.devices[0].user_agent.includes('Chrome') ? 'Chrome'
                                    : session.devices[0].user_agent.includes('Safari') ? 'Safari'
                                    : 'Browser')
                                  : 'Unknown Device'}
                              </span>
                            </div>
                            <p className="text-xs text-content-tertiary">
                              {session.devices?.[0]?.ip_address && `IP: ${session.devices[0].ip_address} · `}
                              {session.authenticated_at && `Signed in ${new Date(session.authenticated_at).toLocaleString()}`}
                            </p>
                            {session.expires_at && (
                              <p className="text-xs text-content-tertiary">
                                Expires {new Date(session.expires_at).toLocaleString()}
                              </p>
                            )}
                          </div>
                          <button
                            onClick={async () => {
                              setRevokingSession(session.id);
                              try {
                                const res = await fetch(`${config.gatewayUrl}/v1/access/me/sessions/${session.id}`, {
                                  method: 'DELETE',
                                  credentials: 'include',
                                });
                                if (res.ok) {
                                  setSessions(prev => prev.filter(s => s.id !== session.id));
                                  setSuccess('Session revoked');
                                }
                              } catch {
                                setError('Failed to revoke session');
                              } finally {
                                setRevokingSession(null);
                              }
                            }}
                            disabled={revokingSession === session.id}
                            className="text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50"
                          >
                            {revokingSession === session.id ? 'Revoking...' : 'Revoke'}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
